// frontend/src/hooks/auth/useRegisterForm.ts

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import { authService, RegisterData } from "../../services/authService";
import { userOnboardingService } from "../../services/userOnboardingService";
import { useApplicationManager } from "../../contexts/ApplicationManagerContext";
import { PasswordValidationResult } from "../../services/passwordValidationService";
import { translations } from "../../components/auth/register/registerTranslations";
import { getValidationSchemas } from "../../components/auth/register/registerValidation";
import {
  RegisterFormValues,
  UseRegisterFormReturn,
  PrefilledData,
} from "../../components/auth/register/registerTypes";

export type { RegisterFormValues } from "../../components/auth/register/registerTypes";

export const useRegisterForm = (): UseRegisterFormReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedPlan =
    new URLSearchParams(location.search).get("plan") || "gratis";
  const { initialize } = useApplicationManager();

  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] =
    useState<PasswordValidationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailExists, setEmailExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [currentLang, setCurrentLang] = useState<string>(
    location.state?.lang || "pt"
  );

  const t = translations[currentLang] || translations.pt;
  const prefilledData: PrefilledData | undefined = location.state?.prefilledData;

  const checkEmailExists = async (email: string): Promise<void> => {
    if (!email || !email.includes("@")) {
      setEmailExists(false);
      return;
    }
    setCheckingEmail(true);
    try {
      const exists = await authService.checkEmailExists(email);
      setEmailExists(exists);
    } catch {
      setEmailExists(false);
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleForgotPassword = () => {
    alert("Funcionalidade de esqueci senha será implementada em breve");
  };

  const goToLogin = () => {
    window.location.href = "/login";
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
    setError(null);
    if (activeStep === 1) {
      setEmailExists(false);
      setCheckingEmail(false);
    }
  };

  useEffect(() => {
    // Redefine apenas os erros de formulário ao mudar idioma, mantendo os valores
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLang]);

  const formik = useFormik<RegisterFormValues>({
    initialValues: {
      fullName: prefilledData?.fullName || "",
      email: prefilledData?.email || "",
      password: prefilledData?.password || "",
      confirmPassword: prefilledData?.password || "",
      projectName: prefilledData?.projectName || "",
      phone: prefilledData?.phoneFormatted || prefilledData?.phone || "",
      website: "",
      plan: preselectedPlan,
    },
    validationSchema: getValidationSchemas(currentLang)[activeStep],
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (activeStep === 0 && emailExists) {
        setError(
          'Este email já está em uso. Use a opção "Esqueci minha senha" se já possui uma conta.'
        );
        return;
      }

      if (activeStep === 0 && passwordValidation && !passwordValidation.isValid) {
        setError(`Senha inválida: ${passwordValidation.errors.join(", ")}`);
        return;
      }

      if (activeStep < Object.values(t.steps).length - 1) {
        setActiveStep(activeStep + 1);
        setError(null);
      } else {
        setLoading(true);
        setError(null);
        try {
          const data: RegisterData = {
            fullName: values.fullName,
            email: values.email,
            password: values.password,
            projectName: values.projectName,
            phone: values.phone,
            website: values.website || undefined,
            plan: values.plan,
          };
          const result = await authService.register(data);
          if (result.success) {
            if (result.data?.nextStep === "email_verification") {
              navigate("/auth/verify-email", {
                replace: true,
                state: {
                  email: result.data.email,
                  userId: result.data.userId,
                  message: result.data.message,
                },
              });
              return;
            }

            // LEGACY: fluxo sem verificação de email
            try {
              if (result.user) {
                await userOnboardingService.createUserDefaultSetup(
                  result.user.id,
                  data.projectName || result.user.name
                );
              }
            } catch {
              // Não bloquear o fluxo se o setup falhar
            }

            await initialize();
            navigate("/owner/dashboard", { replace: true });
          } else {
            setError(result.message ?? t.errorDefault);
          }
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : t.errorDefault;
          setError(msg);
        } finally {
          setLoading(false);
        }
      }
    },
  });

  return {
    formik,
    activeStep,
    showPassword,
    setShowPassword,
    passwordValidation,
    setPasswordValidation,
    loading,
    error,
    emailExists,
    setEmailExists,
    checkingEmail,
    setCheckingEmail,
    currentLang,
    setCurrentLang,
    t,
    prefilledData,
    checkEmailExists,
    handleForgotPassword,
    handleBack,
    goToLogin,
  };
};
